import React, { useContext } from 'react';
import styled from 'styled-components';
import { AppContext } from '@com/core/common/contexts/appContext';
import { useTranslation } from '@com/core/common/translations/i18n';

type TProps = {
    price: number;
    salePrice?: number;
    size?: 'small' /* FBT */ | 'regular' | 'large' /* PDP */;
    decimals?: number;
    isRecommended?: boolean;
};

const ProductPrice = ({
    decimals = 2,
    isRecommended = false,
    price,
    salePrice,
    size = 'regular',
}: TProps): JSX.Element => {
    const { t } = useTranslation('common');
    const { locale } = useContext(AppContext);
    const DISCOUNT_LIMIT = 5;
    const currency = locale.currencyCode;

    const sellingPrice = Number(salePrice || price).toFixed(decimals);
    let oldPrice = null;
    let discountPercent = 0;
    if (salePrice) {
        oldPrice = price > 0 ? Number(price).toFixed(decimals) : 0;
        discountPercent = Math.floor(((price - salePrice) / price) * 100);
    }

    const isLargePrice = size === 'large';

    return (
        <Container className={size}>
            <div className="sellingPriceRow">
                <p className="sellingPrice">
                    <span className="currency">{currency}</span>
                    <strong>{sellingPrice}</strong>
                </p>
                {isLargePrice && <p className="vat">{t('vat-included')}</p>}
            </div>
            {oldPrice && oldPrice > 0 ? (
                <div className="discountRow">
                    <p className="preReductionPrice">
                        {currency} {oldPrice}
                    </p>
                    {discountPercent > DISCOUNT_LIMIT && !isRecommended ? (
                        <p className="discount">
                            {discountPercent}% {t('off')}
                        </p>
                    ) : null}
                </div>
            ) : null}
        </Container>
    );
};

const Container = styled.div`
    margin: 10px 0;
    min-height: 35px;
    line-height: 1;

    .sellingPriceRow {
        display: flex; /* .sellingPrice > .vat */
        align-items: baseline;
    }

    .sellingPrice {
        display: flex;
        align-items: baseline;

        .currency {
            font-size: 0.75rem;
        }

        strong {
            font-size: 1.1875rem;
            ${({ theme: { left } }): string => `margin-${left}: 4px`};
        }
    }

    .discountRow {
        margin-top: 4px;
        display: flex;
        align-items: center;
    }

    .preReductionPrice {
        text-decoration: line-through;
        color: ${({ theme }): string => theme.colors.bodyLight};
        font-size: 0.75rem;
    }

    .discount {
        background-color: ${({ theme }): string => theme.colors.brandSecondary};
        color: ${({ theme }): string => theme.colors.bodyAlt};
        font-size: 0.6875rem;
        padding: 3px 4px;
        border-radius: ${({ theme }): string => theme.metrics.borderRadius};
        ${({ theme: { left } }): string => `margin-${left}: 6px`};
        font-weight: 600;
    }

    &.large {
        .sellingPrice {
            .currency {
                font-size: 0.875rem;
            }

            strong {
                font-size: 2rem;
            }
        }

        .vat {
            color: ${({ theme }): string => theme.colors.bodyLight};
            font-size: 0.75rem;
            position: relative;
            top: -2px;
            ${({ theme: { left } }): string => `margin-${left}: 6px`};
        }

        .discountRow {
            margin-top: 8px;
        }

        .preReductionPrice {
            font-size: 0.875rem;
        }

        .discount {
            font-size: 0.75rem;
            padding: 3px 5px;
            ${({ theme: { left } }): string => `margin-${left}: 8px`};
        }
    }

    &.small {
        .sellingPrice {
            .currency {
                font-size: 0.625rem;
            }

            strong {
                font-size: 0.9375rem;
                ${({ theme: { left } }): string => `margin-${left}: 2px`};
            }
        }
    }
`;

export default ProductPrice;
